// src/App.js
import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { LeadProvider } from './controllers/context/LeadContext';
import { AuthProvider } from './controllers/context/AuthContext';
import { SiteSettingsProvider } from './controllers/context/SiteSettingsContext';
import MainLayout from './views/layouts/MainLayout';
import Home from './views/pages/Home/Home'; // Eagerly load landing page for instant first paint
import './index.css';

// Lazy load secondary routes for optimal initial bundle size and performance
const PropertyListing = lazy(() => import('./views/pages/PropertyListing/PropertyListing'));
const ConstructionServices = lazy(() => import('./views/pages/ConstructionServices/ConstructionServices'));
const Builders = lazy(() => import('./views/pages/Builders/Builders'));
const About = lazy(() => import('./views/pages/About/About'));
const Contact = lazy(() => import('./views/pages/Contact/Contact'));
const PropertyDetail = lazy(() => import('./views/pages/PropertyDetail/PropertyDetail'));
const SellProperty = lazy(() => import('./views/pages/SellProperty/SellProperty'));
const AdminDashboard = lazy(() => import('./views/pages/AdminDashboard/AdminDashboard'));
const UserProfile = lazy(() => import('./views/pages/UserProfile/UserProfile'));
const Reels = lazy(() => import('./views/pages/Reels/Reels'));
const Chat = lazy(() => import('./views/pages/Chat/Chat'));
const Privacy = lazy(() => import('./views/pages/Privacy/Privacy'));
const Terms = lazy(() => import('./views/pages/Terms/Terms'));
const Refund = lazy(() => import('./views/pages/Refund/Refund'));
const Disclaimer = lazy(() => import('./views/pages/Disclaimer/Disclaimer'));
const DeleteAccount = lazy(() => import('./views/pages/DeleteAccount/DeleteAccount'));
const Careers = lazy(() => import('./views/pages/Careers/Careers'));
const Blogs = lazy(() => import('./views/pages/Blogs/Blogs'));
const BlogDetail = lazy(() => import('./views/pages/Blogs/BlogDetail'));
const WhatsAppRedirect = lazy(() => import('./views/pages/WhatsAppRedirect/WhatsAppRedirect'));

// Sleek loading fallback for route chunk loading
const PageLoadingFallback = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justify: 'center',
    minHeight: '60vh',
    color: 'var(--accent)',
    fontSize: '1rem',
    fontWeight: '600',
    gap: '0.75rem'
  }}>
    <div style={{
      width: '24px',
      height: '24px',
      border: '3px solid var(--border-color)',
      borderTopColor: 'var(--accent)',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }} />
    <span>Loading No Brokerage...</span>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <SiteSettingsProvider>
        <LeadProvider>
          <Router>
            <Suspense fallback={<PageLoadingFallback />}>
              <Routes>
                <Route path="/" element={<MainLayout />}>
                  <Route index element={<Home />} />
                  <Route path="buy" element={<PropertyListing type="Buy" />} />
                  <Route path="sell" element={<SellProperty />} />
                  <Route path="rent" element={<PropertyListing type="Rent" />} />
                  <Route path="property/:id" element={<PropertyDetail />} />
                  <Route path="wa/:propertyId" element={<WhatsAppRedirect />} />
                  <Route path="profile" element={<UserProfile />} />
                  <Route path="builders" element={<Builders />} />
                  <Route path="construction" element={<ConstructionServices />} />
                  <Route path="about" element={<About />} />
                  <Route path="contact" element={<Contact />} />
                  <Route path="reels" element={<Reels />} />
                  <Route path="careers" element={<Careers />} />
                  <Route path="blogs" element={<Blogs />} />
                  <Route path="blogs/:slug" element={<BlogDetail />} />
                  <Route path="chat" element={<Chat />} />
                  <Route path="privacy" element={<Privacy />} />
                  <Route path="terms" element={<Terms />} />
                  <Route path="refund" element={<Refund />} />
                  <Route path="disclaimer" element={<Disclaimer />} />
                  <Route path="delete-account" element={<DeleteAccount />} />
                  <Route path="admin" element={<AdminDashboard />} />
                </Route>
              </Routes>
            </Suspense>
          </Router>
        </LeadProvider>
      </SiteSettingsProvider>
    </AuthProvider>
  );
}

export default App;
